/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/PinMemberRow.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Component for rendering a PIN member row in the form table.
────────────────────────────────────────────────────────────────*/

import { Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { PinMember, ValidationErrors } from '../types';
import { useI18n } from '../../../hooks/useI18n';

interface PinMemberRowProps {
  member: PinMember;
  index: number;
  onUpdate: (index: number, field: keyof PinMember, value: PinMember[keyof PinMember]) => void;
  onRemove: (index: number) => void;
  errors: ValidationErrors;
}

export function PinMemberRow({
  member,
  index,
  onUpdate,
  onRemove,
  errors
}: PinMemberRowProps) {
  const { t } = useI18n();

  return (
    <tr className="border-b border-border">
      <td className="py-2 px-4">
        <Input
          type="text"
          value={member.pin}
          onChange={(event) => onUpdate(index, 'pin', event.target.value)}
          placeholder={t('pinLists.fields.pin.placeholder')}
          className={errors[`member_${index}_pin`] ? 'border-red-500' : ''}
        />
        {errors[`member_${index}_pin`] && (
          <p className="text-xs text-red-500 mt-1">{errors[`member_${index}_pin`]}</p>
        )}
      </td>
      <td className="py-2 px-4">
        <Input
          type="text"
          value={member.description}
          onChange={(event) => onUpdate(index, 'description', event.target.value)}
          placeholder={t('pinLists.fields.pinDescription.placeholder')}
        />
      </td>
      <td className="text-center py-2 px-4">
        <div className="flex items-center justify-center gap-2">
          <Toggle
            value={member.enabled}
            onChange={(checked) => onUpdate(index, 'enabled', checked)}
          />
          <SimpleTooltip content={t('common.delete')}>
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
            >
              <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
            </button>
          </SimpleTooltip>
        </div>
      </td>
    </tr>
  );
}

